import * as React from "react";
import {
  css,
  styled,
  Block,
  Media,
  BorderRadiusBase,
  Button,
  unit,
  keyframes,
} from "../../../seenspire-library";
import {
  TRANSITION_DURATION,
  SCALE_AMOUNT,
  SIDE_PADDING,
  CUSTOM_BREAKPOINTS,
} from "./variables";
import { SliderItemStatusType } from "./interfaces";

// Hand-written rather than imported via SVGR's `ReactComponent` transform:
// SVGR caches this SVG's static child elements in a module-scoped variable
// and reuses the same React element object across every render *and every
// simultaneously-mounted instance* of the component. React 19's reconciler
// doesn't tolerate that the way React 18 did - only the first of several
// placeholder boxes rendered at once would actually show anything, the rest
// silently rendered nothing with no thrown error. Plain JSX creates a fresh
// element per render/instance, sidestepping the issue entirely.
const ChannelPlaceholder = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={284.44444444444446}
    height={160}
    viewBox="0 0 284.44444444444446 160"
    {...props}
  >
    <g id="ChannelPlaceholder">
      <rect id="frame" fillOpacity={0.3} x={0} y={0} width={284.44444444444446} height={160} rx={4} />
      <rect id="bar" fillOpacity={0.2} x={58} y={122} width={164} height={18} rx={4} />
    </g>
  </svg>
);

const ChannelPlaceholderDraft = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={285}
    height={160}
    viewBox="0 0 285 160"
    fill="none"
    {...props}
  >
    <g id="ChannelPlaceholder">
      <rect id="frame" x={0} y={0} width={284.44444444444446} height={160} rx={4} />
    </g>
    <rect opacity={0.86} x={112} y={49} width={59.8757} height={59} rx={29.5} fill="#1E314F" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M151.483 77.8799C152.127 78.2563 152.124 78.8684 151.483 79.2431L136.98 88.5177C136.336 88.8941 135.814 88.5968 135.814 87.8523V69.2707C135.814 68.5268 136.339 68.2306 136.98 68.6053L151.483 77.8799Z"
      fill="#4F5D73"
    />
  </svg>
);

export const SLIDER_ITEM_ACTIVE_SHADOW = "0px 0px 5px #00000030";

export const FadeInAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
export const FadeInAnimationStyle = css`
  animation: ${FadeInAnimation} 400ms ease-in-out 1;
  animation-fill-mode: forwards;
`;
export const FadeOutAnimation = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;
export const FadeOutAnimationStyle = css`
  animation: ${FadeOutAnimation} 400ms ease-in-out 1;
  animation-fill-mode: forwards;
`;
export const SliderTransition = css`
  transition: all ${TRANSITION_DURATION}ms ease-in-out;
  /* Defines the easing of the transition,
  to modify use: http://cubic-bezier.com/#1,.16,.49,.43 */
  /* transition-timing-function: cubic-bezier(0.5,0,0.1,1);  */
`;

/* Controls the aspect ratio of the item */
export const AspectRatioBox = styled(Block)<{
  ratioType?: "1:1" | "16:9" | "4:3";
}>`
  width: 100%;
  height: 0;
  padding: ${({ ratioType }) => {
    switch (ratioType) {
      case "1:1":
        return "50% 0";
      case "16:9":
        return "28.125% 0";
      case "4:3":
        return "75% 0 0 0";
      default:
        return "28.125% 0";
    }
  }};
`;

export const ChannelPlaceholderStyled = styled(ChannelPlaceholder)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  g#ChannelPlaceholder {
    fill: #30415b;
  }
`;

export const ChannelPlaceholderDraftStyled = styled(ChannelPlaceholderDraft)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  g#ChannelPlaceholder {
    fill: #30415b;
  }
`;

const SliderItemContentTyped = styled(Block)<{
  isActive: boolean;
  isInView?: boolean;
  isPlaceholder?: boolean;
  isOpened?: boolean;
  gridMode?: boolean;
}>``;
export const SliderItemContent = styled(SliderItemContentTyped)`
  position: relative;
  z-index: 0;
  ${BorderRadiusBase};
  overflow: hidden;
`;

export const CONTENT_SPACING = 11;

export const ItemOnHoverContainerBase = styled(Block)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
`;

//`${unit(theme.borderRadius)} ${unit(theme.borderRadius)} 0 0`};
export const ItemOnHoverDescriptionContainer = styled(Block)<{
  isOpened?: boolean;
  isActive?: boolean;
}>`
  position: relative;
  z-index: ${({ theme }) => theme.header.zIndex - 10};
  overflow: hidden;
  border-radius: ${({ theme, isOpened }) =>
    isOpened
      ? `${unit(theme.borderRadius)} ${unit(theme.borderRadius)} 0 0`
      : unit(theme.borderRadius)};
  box-shadow: ${({ theme, isActive }) =>
    isActive ? SLIDER_ITEM_ACTIVE_SHADOW : "0px 0px transparent"};
`;

export const ItemText = styled(Block)`
  text-shadow: ${({ theme }) => theme.shadows.lighter};
`;

export const DescriptionText = styled(Block)`
  font-size: ${({ theme }) => theme.fontSize.small};
`;

export const ItemOnHoverDescriptionContainerInner = styled(
  ItemOnHoverContainerBase
)`
  text-align: center;
  padding: 0 ${({ theme }) => unit(theme.spacing.base * 3)};
  z-index: 10;
  display: ${({ display }) => display || "flex"};
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (min-width: ${({ theme }) =>
      theme.mediaSizes
        .xlarge}px) and (max-width: ${CUSTOM_BREAKPOINTS.BETWEEN_LARGE_AND_XLARGE}px) {
    transform: scale(0.8);
    ${DescriptionText} {
      font-size: ${({ theme }) => theme.fontSize.smaller};
    }
  }
`;

export const SliderItemOnHoverContentInner = styled(Block)<{
  isActive: boolean;
}>`
  position: relative;
  z-index: ${({ theme }) => theme.header.zIndex - 5};
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: ${({ theme }) =>
    `0 0 ${unit(theme.borderRadius)} ${unit(theme.borderRadius)}`};
  box-shadow: ${({ isActive }) =>
    isActive ? SLIDER_ITEM_ACTIVE_SHADOW : "0px 0px transparent"};
  /* ${({ isActive }) =>
    isActive ? FadeInAnimationStyle : FadeOutAnimationStyle}; */
`;

export const SliderItemOnHoverContainer = styled(Block)`
  position: absolute;
  height: 100%;
  top: 0;
  left: 0px;
  right: 0px;
  display: grid;
  cursor: pointer;
`;
/* contains elements to show when on hover  */
export const SliderItemOnHoverContentTyped = styled(Block)<{
  isActive: boolean;
  isFirst: boolean;
  isLast: boolean;
  isOpened: boolean;
  skipInnerInteraction?: boolean;
  hoverDirection?: "up" | "down";
}>``;
export const SliderItemOnHoverContent = styled(SliderItemOnHoverContentTyped)`
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: absolute;
  ${({ isActive }) => (isActive ? FadeInAnimationStyle : FadeOutAnimationStyle)}
  ${SliderTransition}
  transition-duration: 400ms;
  pointer-events: ${({ isActive }) => !isActive && "none"};
  transform: ${({ isActive, isOpened, hoverDirection }) => {
    // Only apply custom transform when hoverDirection is explicitly set to "down"
    if (hoverDirection === "down") {
      return (isActive || isOpened)
        ? "translateY(0)"  // Visible: in normal position
        : "translateY(-100%)";  // Hidden: above normal position for downward expansion
    }
    // Default behavior (original): no transform, let CSS handle the positioning
    return "none";
  }};
  ${SliderItemOnHoverContentInner} {
    transform: ${({ isActive, isOpened, skipInnerInteraction, hoverDirection }) => {
      // Only apply custom transform when hoverDirection is explicitly set to "down"
      if (hoverDirection === "down") {
        return (isActive || isOpened) && !skipInnerInteraction
          ? `translateY(0)` 
          : `translateY(-100%)`;  // Hidden above for downward slide
      }
      // Default behavior (original): no transform
      return "none";
    }};
    opacity: ${({ isActive, isOpened, skipInnerInteraction }) =>
      (isActive || isOpened) || skipInnerInteraction ? 1 : 0};
    visibility: ${({ isActive, isOpened, skipInnerInteraction }) =>
      (isActive || isOpened) || skipInnerInteraction ? "visible" : "hidden"};
    /* ${({ isActive }) =>
      isActive ? FadeInAnimationStyle : FadeOutAnimationStyle}
    ${SliderTransition}
    transition-duration: 450ms;
    transition-delay: 100ms; */
  }
`;

export const CircleContainer = styled(Block).attrs({})<{
  circleSize?: "larger" | "large" | "medium" | "base" | "small";
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  min-width: 34px;
  min-height: 34px;
  max-width: 90px;
  max-height: 90px;
  transform: ${({ circleSize }) => {
    switch (circleSize) {
      case "larger":
        return "scale(3)";
      case "large":
        return "scale(1.5)";
      case "medium":
        return "scale(2.2)";
      default:
        return `scale(1)`;
    }
  }};
  background-color: ${({ theme, bg }) => bg || theme.colors.white.base};
`;
export const ItemOnHoverBackgroundContainer = styled(ItemOnHoverContainerBase)`
  z-index: 1;
  overflow: hidden;
`;

export const SliderItemButton = styled(Button)`
  position: relative;
  height: ${({ theme }) => unit(theme.element.height / SCALE_AMOUNT)};
  cursor: ${({ opacity }) => (opacity === 0.5 ? `default` : `pointer`)};
`;

export const SliderItemButtonIcon = styled(Block).attrs({})<{
  status?: SliderItemStatusType;
}>`
  z-index: 10;
  position: absolute;
  top: -1px;
  right: -1px;
  width: ${({ theme, status }) =>
    status === "DONE" ? "100%" : unit(theme.element.height / SCALE_AMOUNT)};
  height: calc(100% + 2px);
  justify-content: center;
  align-items: center;
  display: flex;
`;

export const PaddedContainer = styled(Block)`
  z-index: ${({ zIndex }) => zIndex || 3};
  position: relative;
  padding: ${({ padding }) => !padding && `0 ${SIDE_PADDING.default}%`};
  ${Media.at.small`padding: 0 ${SIDE_PADDING.small}%`};
  touch-action: pan-y;
`;

export const UnlockButtonStyles = css`
  background-color: ${({ customBackgroundColor, theme }: any) =>
    customBackgroundColor && theme.layout.customColors.upgradeColor.base};
  border-color: ${({ customBackgroundColor, theme }: any) =>
    customBackgroundColor && theme.layout.customColors.upgradeColor.base};
  &:hover {
    background-color: ${({ customBackgroundColor, theme }: any) =>
      customBackgroundColor && theme.layout.customColors.upgradeColor.dark};
    border-color: ${({ customBackgroundColor, theme }: any) =>
      customBackgroundColor && theme.layout.customColors.upgradeColor.dark};
  }
  svg {
    fill: #060a12;
  }
`;